// bt-common/engine/identity.ts
// #922 S0a: engine instance + sender identity types. Types and pure constructors only; nothing is persisted.

declare const brand: unique symbol;
export type Brand<T, B extends string> = T & { readonly [brand]: B };

/** A WhatsApp phone number the account sends from (`wa_senders._id` once S0b lands). */
export type SenderId = Brand<string, 'SenderId'>;
/** One running transport instance: `session:<sessionsId>` or `client:<mqttClientId>`. */
export type EngineId = Brand<string, 'EngineId'>;

export type EngineRef =
    | { source: 'session'; sessionId: string }
    | { source: 'client'; clientId: string };

const SESSION_PREFIX = 'session:';
const CLIENT_PREFIX = 'client:';

/** The only constructor of an EngineId. Empty ids are rejected. */
export function engineIdOf(ref: EngineRef): EngineId {
    const id = ref.source === 'session' ? ref.sessionId : ref.clientId;
    if (id.length === 0) throw new Error('engineIdOf: empty id');
    const minted: string = `${ref.source === 'session' ? SESSION_PREFIX : CLIENT_PREFIX}${id}`;
    if (!isEngineId(minted)) throw new Error('engineIdOf: unreachable');
    return minted;
}

/** Shape guard: the narrowing that lets {@link engineIdOf} return a brand without a cast. */
export function isEngineId(v: string): v is EngineId {
    return (v.startsWith(SESSION_PREFIX) && v.length > SESSION_PREFIX.length) || (v.startsWith(CLIENT_PREFIX) && v.length > CLIENT_PREFIX.length);
}

/** Brands a non-empty string as a SenderId (shape check only; existence is checked by the registry in S0b). */
export function isSenderId(v: string): v is SenderId {
    return v.length > 0;
}

/** Typed inverse of {@link engineIdOf}; null for anything it did not produce. */
export function parseEngineId(id: string): EngineRef | null {
    if (id.startsWith(SESSION_PREFIX) && id.length > SESSION_PREFIX.length) return { source: 'session', sessionId: id.slice(SESSION_PREFIX.length) };
    if (id.startsWith(CLIENT_PREFIX) && id.length > CLIENT_PREFIX.length) return { source: 'client', clientId: id.slice(CLIENT_PREFIX.length) };
    return null;
}
